/**
 * Attendance ↔ course linking (standard in this app)
 * -------------------------------------------------
 * When marking attendance (`CourseSelector` → `markAttendance`), each `Attendance`
 * document is written with:
 *   - `courseName`: **exactly** `selectedCourse.name` from the `Courses` collection
 *   - `courseId`: `selectedCourse.id` (Firestore document id of that course row)
 *   - `campusCode`: selected campus
 *
 * Queries that load attendance by subject should use that same **`Courses.name`**
 * string, not test labels, Urdu titles, or raw numeric ids.
 *
 * Tests (`Tests` collection) carry `courseId` (sometimes number vs string). Resolve
 * it to the course row first and prefer `course.name`; use `test.courseName` only
 * as a fallback when the course row is missing.
 */

/**
 * @param {string|number|null|undefined} courseIdKey - `test.courseId` or course group key
 * @param {Array<{ id?: string, name?: string }>} courses - e.g. `coursesForCampus` from context
 * @param {Array<{ courseId?: string|number, courseName?: string }>} tests - gradebook / context tests
 * @returns {string} Value to use for `where('courseName', '==', …)` and display; matches marked attendance when data is consistent
 */
export function attendanceCourseNameFromTestCourseId(courseIdKey, courses, tests) {
  if (courseIdKey == null || courseIdKey === 'default') {
    const t = (tests || []).find((x) => x.courseName) || (tests || [])[0];
    return t?.courseName || String(courseIdKey ?? '');
  }
  const keyStr = String(courseIdKey);
  const course = (courses || []).find(
    (c) => c.id === courseIdKey || String(c.id) === keyStr
  );
  if (course?.name) return course.name;
  const testRow = (tests || []).find(
    (t) =>
      (t.courseId === courseIdKey || String(t.courseId) === keyStr) &&
      t.courseName
  );
  if (testRow?.courseName) return testRow.courseName;
  return keyStr;
}
